/**
 * Family/Friends Pty group affordability calculator: what each person can
 * afford alone and what the group can afford together. Runs fully in the
 * browser (maths in utils/groupAffordability.ts, shared with the public
 * Affordability Calculator via calculateMaxLoan) and stores nothing
 * server-side by itself. The "WhatsApp me my results" panel under the group
 * card (CalculatorLeadCapture) sends the inputs + results with the lead only
 * when the visitor asks for them.
 *
 * "Register your Family/Friends Pty" hands the names and the agreed
 * shareholding split to the registration wizard through localStorage
 * (LS_CALCULATOR_STORE) plus a `familyPty:handoff` window event.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { ArrowRight, Calculator, Plus, Trash2, User } from 'lucide-react';
import { db } from '../../services/firebase';
import {
  DEFAULT_RATE_OFFSET_FROM_PRIME,
  DEFAULT_TERM_YEARS,
  DEFAULT_PROPERTY_DEPOSIT,
  defaultDepositShares,
  FALLBACK_PRIME,
  calculateGroupAffordability,
  formatRand,
  type GroupPersonInput,
  type GroupPersonResult,
} from '../../utils/groupAffordability';
import { formatPct, type CalculatorHandoff } from '../../utils/companyRegistrationShared';
import { scrollToAnchor } from '../../utils/scrollToAnchor';
import { trackEvent } from '../../utils/analytics';
import BrochureDownload from './BrochureDownload';
import CalculatorLeadCapture, { type CalculatorCapturePayload } from './CalculatorLeadCapture';
import { FieldError, inputClass } from './fields';
import { writeHandoff } from './registrationState';

const MAX_PEOPLE = 6;
export const CALCULATOR_ANCHOR = 'family-pty-calculator';
export const APPLY_ANCHOR = 'family-pty-apply';

interface PersonRow extends Omit<GroupPersonInput, 'grossIncome' | 'livingExpenses'> {
  key: number;
  grossIncome: number | null;
  livingExpenses: number | null;
  /** The visitor typed their own deposit — never overwrite it with the default share. */
  depositEdited: boolean;
}

let rowSeq = 0;
const newRow = (deposit = DEFAULT_PROPERTY_DEPOSIT): PersonRow => ({ key: ++rowSeq, name: '', grossIncome: null, livingExpenses: null, debtRepayments: 0, deposit, depositEdited: false });

/** Re-split the R30 000 default deposit across the rows nobody has edited. */
function withDefaultDeposits(rows: PersonRow[]): PersonRow[] {
  const shares = defaultDepositShares(rows.length);
  return rows.map((r, i) => (r.depositEdited ? r : { ...r, deposit: shares[i] }));
}

const fmtInput = (v: number | null) => (v === null ? '' : v.toLocaleString('en-ZA'));
const parseAmount = (s: string): number | null => {
  const digits = s.replace(/[^\d]/g, '');
  return digits ? parseInt(digits, 10) : null;
};

function RandInput({ id, label, value, onChange }: { id: string; label: string; value: number | null; onChange: (v: number | null) => void }) {
  const [text, setText] = useState(fmtInput(value));
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (document.activeElement !== ref.current) setText(fmtInput(value));
  }, [value]);
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-gray-700 mb-1.5">{label}</label>
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm font-medium" aria-hidden="true">R</span>
        <input
          ref={ref}
          id={id}
          type="text"
          inputMode="numeric"
          value={text}
          placeholder="0"
          onChange={(e) => {
            setText(e.target.value);
            onChange(parseAmount(e.target.value));
          }}
          onBlur={() => setText(fmtInput(value))}
          className={`${inputClass()} pl-8`}
        />
      </div>
    </div>
  );
}

/** Per-person result card. */
export function AffordabilityPersonCard({ result }: { result: GroupPersonResult }) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4">
      <p className="font-semibold text-gray-900 flex items-center gap-2 min-w-0">
        <User className="w-4 h-4 text-primary shrink-0" aria-hidden="true" />
        <span className="truncate">{result.name}</span>
      </p>
      <dl className="mt-3 space-y-1.5 text-sm">
        <div className="flex justify-between gap-3"><dt className="text-gray-500">Maximum bond</dt><dd className="font-semibold text-gray-900">{formatRand(result.maxBond)}</dd></div>
        <div className="flex justify-between gap-3"><dt className="text-gray-500">Monthly instalment</dt><dd className="font-semibold text-gray-900">{formatRand(result.maxInstalment)}</dd></div>
        <div className="flex justify-between gap-3 pt-1.5 border-t border-gray-100"><dt className="text-gray-700 font-medium">Purchase price</dt><dd className="font-bold text-primary">{formatRand(result.purchasePrice)}</dd></div>
      </dl>
    </div>
  );
}

interface GroupAffordabilityCalculatorProps {
  /** embedded: on /family-pty (scrolls to the wizard); standalone: /family-pty/calculator (navigates). */
  mode: 'embedded' | 'standalone';
}

export default function GroupAffordabilityCalculator({ mode }: GroupAffordabilityCalculatorProps) {
  const navigate = useNavigate();
  const [rows, setRows] = useState<PersonRow[]>(() => [newRow()]);
  const [prime, setPrime] = useState<number | null>(null);
  const [rateText, setRateText] = useState(String(FALLBACK_PRIME + DEFAULT_RATE_OFFSET_FROM_PRIME));
  const [rateEdited, setRateEdited] = useState(false);
  const [termText, setTermText] = useState(String(DEFAULT_TERM_YEARS));
  const [splitText, setSplitText] = useState<string[] | null>(null);

  // Live prime from the same source as the public Affordability Calculator.
  useEffect(() => {
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'systemSettings', 'commissions'));
        const rate = snap.exists() ? snap.data()?.currentInterestRate : null;
        if (typeof rate === 'number' && rate > 0) setPrime(rate);
      } catch {
        // Fall back to the default prime.
      }
    })();
  }, []);

  useEffect(() => {
    if (prime !== null && !rateEdited) setRateText(String(Math.round((prime + DEFAULT_RATE_OFFSET_FROM_PRIME) * 100) / 100));
  }, [prime, rateEdited]);

  const rate = Number(rateText.replace(',', '.'));
  const term = Number(termText);
  const rateOk = isFinite(rate) && rate > 0 && rate <= 30;
  const termOk = Number.isInteger(term) && term >= 1 && term <= 30;

  const result = useMemo(
    () =>
      calculateGroupAffordability(
        rows.map((r) => ({ name: r.name, grossIncome: r.grossIncome ?? 0, livingExpenses: r.livingExpenses ?? 0, debtRepayments: r.debtRepayments, deposit: r.deposit })),
        rateOk ? rate : FALLBACK_PRIME + DEFAULT_RATE_OFFSET_FROM_PRIME,
        termOk ? term : DEFAULT_TERM_YEARS
      ),
    [rows, rate, rateOk, term, termOk]
  );

  // A changed number of people resets any hand-edited split.
  useEffect(() => setSplitText(null), [rows.length]);

  const split: number[] = splitText ? splitText.map((t) => Number(t.replace(',', '.'))) : result.suggestedSplit;
  const splitTotal = Math.round(split.reduce((s, x) => s + (isFinite(x) ? x : 0), 0) * 100) / 100;
  const splitValid = split.every((x) => isFinite(x) && x > 0 && x <= 100) && Math.abs(splitTotal - 100) <= 0.01;
  const hasIncome = rows.some((r) => (r.grossIncome ?? 0) > 0);

  const update = (key: number, patch: Partial<PersonRow>) => setRows((rs) => rs.map((r) => (r.key === key ? { ...r, ...patch } : r)));

  const calculatorSnapshot = (): CalculatorCapturePayload => ({
    ratePct: rateOk ? rate : FALLBACK_PRIME + DEFAULT_RATE_OFFSET_FROM_PRIME,
    termYears: termOk ? term : DEFAULT_TERM_YEARS,
    people: result.people.slice(0, MAX_PEOPLE).map((p, i) => {
      const r = rows[i];
      return {
        name: r?.name.trim() || p.name,
        grossIncome: r?.grossIncome ?? 0,
        livingExpenses: r?.livingExpenses ?? 0,
        debtRepayments: r?.debtRepayments ?? 0,
        deposit: r?.deposit ?? 0,
        maxBond: Math.round(p.maxBond),
        maxInstalment: Math.round(p.maxInstalment),
        purchasePrice: Math.round(p.purchasePrice),
      };
    }),
    totals: {
      bond: Math.round(result.totalBond),
      instalment: Math.round(result.totalInstalment),
      purchasePrice: Math.round(result.totalPurchasePrice),
      bestSinglePurchasePrice: Math.round(result.bestSinglePurchasePrice),
    },
    split: (splitValid ? split : result.suggestedSplit).slice(0, MAX_PEOPLE),
  });

  const register = () => {
    if (!splitValid) return;
    const handoff: CalculatorHandoff = {
      people: result.people.map((p, i) => ({ name: rows[i]?.name.trim() || p.name, shareholdingPct: Math.round(split[i] * 100) / 100 })),
      savedAtMs: Date.now(),
    };
    writeHandoff(handoff);
    window.dispatchEvent(new Event('familyPty:handoff'));
    trackEvent('family_pty_calculator_register', { people: rows.length, mode });
    if (mode === 'embedded') scrollToAnchor(APPLY_ANCHOR);
    else navigate('/family-pty/apply');
  };

  return (
    <div className="space-y-6">
      {/* Inputs */}
      <div className="space-y-4">
        {rows.map((r, i) => {
          const base = `calc-${r.key}`;
          return (
            <fieldset key={r.key} className="bg-white border border-gray-200 rounded-2xl p-4 sm:p-5 shadow-card min-w-0">
              <legend className="sr-only">Person {i + 1}</legend>
              <div className="flex items-center justify-between gap-3 mb-4">
                <p className="font-bold text-gray-900 flex items-center gap-2 min-w-0">
                  <span className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0"><User className="w-4 h-4 text-primary" aria-hidden="true" /></span>
                  <span className="truncate">{r.name.trim() || `Person ${i + 1}`}</span>
                </p>
                {i > 0 && (
                  <button type="button" onClick={() => setRows((rs) => withDefaultDeposits(rs.filter((x) => x.key !== r.key)))} className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-red-600 font-medium shrink-0">
                    <Trash2 className="w-4 h-4" aria-hidden="true" /> Remove
                  </button>
                )}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <label htmlFor={`${base}-name`} className="block text-sm font-medium text-gray-700 mb-1.5">First name <span className="text-gray-400 font-normal">(optional)</span></label>
                  <input id={`${base}-name`} type="text" autoComplete="given-name" value={r.name} placeholder={`Person ${i + 1}`} onChange={(e) => update(r.key, { name: e.target.value })} className={inputClass()} />
                </div>
                <RandInput id={`${base}-gross`} label="Gross monthly income" value={r.grossIncome} onChange={(v) => update(r.key, { grossIncome: v })} />
                <RandInput id={`${base}-expenses`} label="Monthly living expenses" value={r.livingExpenses} onChange={(v) => update(r.key, { livingExpenses: v })} />
                <RandInput id={`${base}-debt`} label="Existing monthly debt repayments" value={r.debtRepayments} onChange={(v) => update(r.key, { debtRepayments: v ?? 0 })} />
                <RandInput id={`${base}-deposit`} label="Deposit contribution" value={r.deposit} onChange={(v) => update(r.key, { deposit: v ?? 0, depositEdited: true })} />
              </div>
            </fieldset>
          );
        })}
        {rows.length < MAX_PEOPLE && (
          <button
            type="button"
            onClick={() => setRows((rs) => withDefaultDeposits([...rs, newRow()]))}
            className="w-full inline-flex items-center justify-center gap-2 border-2 border-dashed border-primary/50 text-primary font-semibold rounded-2xl py-3.5 hover:bg-primary-light/50 transition-colors"
          >
            <Plus className="w-5 h-5" aria-hidden="true" /> Add another person
          </button>
        )}

        <p className="text-xs text-gray-500 px-1">
          Deposit: we start with {formatRand(DEFAULT_PROPERTY_DEPOSIT)} for the property, shared equally between everyone. Change any amount to what that person will actually put in.
        </p>

        <div className="bg-white border border-gray-200 rounded-2xl p-4 sm:p-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="calc-rate" className="block text-sm font-medium text-gray-700 mb-1.5">Interest rate (% per year)</label>
            <input
              id="calc-rate"
              type="number"
              inputMode="decimal"
              step="0.05"
              min={0}
              value={rateText}
              onChange={(e) => {
                setRateEdited(true);
                setRateText(e.target.value);
              }}
              className={inputClass(!rateOk)}
            />
            {rateOk ? (
              <p className="mt-1.5 text-xs text-gray-500">{`Prime${prime !== null ? ` (${prime}%)` : ''} minus 0.5% by default.`} Change it to match your bank.</p>
            ) : (
              <FieldError message="Enter an interest rate between 0 and 30%." />
            )}
          </div>
          <div>
            <label htmlFor="calc-term" className="block text-sm font-medium text-gray-700 mb-1.5">Loan term (years)</label>
            <input id="calc-term" type="number" inputMode="numeric" min={1} max={30} step={1} value={termText} onChange={(e) => setTermText(e.target.value)} className={inputClass(!termOk)} />
            {!termOk && <FieldError message="Enter a term between 1 and 30 years." />}
          </div>
        </div>
      </div>

      {/* Results */}
      {!hasIncome ? (
        <p className="flex items-center gap-2 text-sm text-gray-500 bg-white border border-gray-200 rounded-2xl p-4">
          <Calculator className="w-5 h-5 text-primary shrink-0" aria-hidden="true" />
          Enter at least one gross monthly income to see what you can afford alone and together.
        </p>
      ) : (
        <div className="space-y-5" aria-live="polite">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {result.people.map((p, i) => <AffordabilityPersonCard key={rows[i]?.key ?? i} result={p} />)}
          </div>

          <div className="bg-gray-900 text-white rounded-2xl p-5 sm:p-7">
            <p className="text-lg sm:text-2xl font-bold leading-snug">
              On your own: <span className="text-white/80">{formatRand(result.bestSinglePurchasePrice)}</span>.{' '}
              Together: <span className="text-[#8fd16a]">{formatRand(result.totalPurchasePrice)}</span>.
            </p>
            <dl className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
              <div className="bg-white/10 rounded-xl p-3"><dt className="text-white/60">Combined bond</dt><dd className="font-semibold text-base">{formatRand(result.totalBond)}</dd></div>
              <div className="bg-white/10 rounded-xl p-3"><dt className="text-white/60">Combined instalment</dt><dd className="font-semibold text-base">{formatRand(result.totalInstalment)} <span className="text-white/60 font-normal">/ month</span></dd></div>
              <div className="bg-white/10 rounded-xl p-3"><dt className="text-white/60">Combined purchase price</dt><dd className="font-semibold text-base">{formatRand(result.totalPurchasePrice)}</dd></div>
            </dl>
            <div className="mt-5 space-y-2.5">
              {result.people.map((p, i) => {
                const pct = result.totalPurchasePrice > 0 ? (p.purchasePrice / result.totalPurchasePrice) * 100 : 0;
                return (
                  <div key={rows[i]?.key ?? i}>
                    <div className="flex justify-between gap-3 text-xs text-white/70 mb-1">
                      <span className="truncate">{p.name}</span>
                      <span className="shrink-0">{formatRand(p.purchasePrice)}</span>
                    </div>
                    <div className="h-2.5 rounded-full bg-white/10 overflow-hidden">
                      <div className="h-full rounded-full bg-[#8fd16a]" style={{ width: `${Math.max(0, Math.min(100, pct))}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <CalculatorLeadCapture defaultFirstName={rows[0]?.name.trim() ?? ''} getCalculator={calculatorSnapshot} mode={mode} />

          <div className="text-center">
            <BrochureDownload source="calculator" label="See how the Group Millionaire Strategy works" variant="outline" />
          </div>

          {/* Suggested shareholding */}
          <div className="bg-white border border-gray-200 rounded-2xl p-4 sm:p-5">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h4 className="font-bold text-gray-900">Suggested shareholding</h4>
                <p className="mt-1 text-sm text-gray-500">In proportion to what each person brings (deposit plus maximum bond). You can change it.</p>
              </div>
              {splitText && (
                <button type="button" onClick={() => setSplitText(null)} className="text-sm text-primary font-semibold hover:underline shrink-0">Reset</button>
              )}
            </div>
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {result.people.map((p, i) => (
                <div key={rows[i]?.key ?? i}>
                  <label htmlFor={`calc-split-${i}`} className="block text-sm font-medium text-gray-700 mb-1.5 truncate">{p.name}</label>
                  <div className="relative">
                    <input
                      id={`calc-split-${i}`}
                      type="number"
                      inputMode="decimal"
                      step="0.01"
                      min={0}
                      max={100}
                      value={splitText ? splitText[i] : String(result.suggestedSplit[i])}
                      onChange={(e) => {
                        const base = splitText ?? result.suggestedSplit.map(String);
                        const nextSplit = [...base];
                        nextSplit[i] = e.target.value;
                        setSplitText(nextSplit);
                      }}
                      className={`${inputClass(!splitValid)} pr-9`}
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" aria-hidden="true">%</span>
                  </div>
                </div>
              ))}
            </div>
            <p className={`mt-3 text-sm ${splitValid ? 'text-gray-600' : 'text-red-600'}`}>
              Total: <strong>{formatPct(splitTotal)}%</strong>
              {!splitValid && ' — the shareholding must add up to 100%, with every person above 0%.'}
            </p>
          </div>

          <div className="text-center">
            <button
              type="button"
              onClick={register}
              disabled={!splitValid}
              className="inline-flex items-center justify-center gap-2 bg-primary hover:bg-primary-dark text-white font-semibold px-8 py-3.5 rounded-full transition-colors shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Register your Family/Friends Pty <ArrowRight className="w-5 h-5" aria-hidden="true" />
            </button>
          </div>

          <p className="text-xs text-gray-500 leading-relaxed text-center max-w-2xl mx-auto">
            This is an estimate only, not a pre-qualification or an offer. Actual bond approval depends on the bank's assessment of the company and the people behind it.
          </p>
        </div>
      )}
    </div>
  );
}
